/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { callApi } from 'api/helpers/api.helpers';
import { useEffect, useState } from 'react';
import addressBookApi from '../../../api/addressBook.api';
import useWindowUnloadEffect from '../../../shared/utils/hooks/useWindowUnload';

export default function useDuplicateContactDialog(partyResult, onAllDuplicatesResolved, onCancelSave) {
  const [strategy, setStrategy] = useState(null);
  const [applyToAll, setApplyToAll] = useState(false);
  const [duplicatesPage, setDuplicatesPage] = useState(null);
  const [currentDuplicateIndex, setCurrentDuplicateIndex] = useState(0);
  const [resolvedList, setResolvedList] = useState([]);
  const [resolvedAll, setResolvedAll] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const onCancel = () => {
    setStrategy(null);
    setApplyToAll(false);
    setDuplicatesPage(null);
    setCurrentDuplicateIndex(0);
    setResolvedList([]);
    setResolvedAll(false);
    onCancelSave();
  };
  useWindowUnloadEffect(() => onCancel(), true);

  const currentDuplicate = duplicatesPage?.content?.[currentDuplicateIndex];
  const isLastDuplicate = currentDuplicateIndex + 1 === duplicatesPage?.content?.length;
  const hasAnotherPage = !duplicatesPage?.last;

  const getDuplicatesPage = async (page, pageSize) => {
    const duplicatesResponse = await callApi(addressBookApi.getPartyDuplicates)(partyResult.id, page, pageSize);
    setDuplicatesPage(duplicatesResponse);
    setCurrentDuplicateIndex(currentDuplicateIndex);
  };

  useEffect(() => {
    getDuplicatesPage(0, 10);
  }, [partyResult]);

  useEffect(() => {
    if (resolvedAll) {
      onAllDuplicatesResolved(resolvedList);
    }
  }, [resolvedAll]);

  const storeResolvedList = (newResolvedList) => {
    const newList = [...resolvedList, ...newResolvedList];
    setResolvedList(newList);
  };

  const loadNextPage = (numberOfItemsToLoad) => {
    if (hasAnotherPage) {
      getDuplicatesPage(duplicatesPage.number + 1, numberOfItemsToLoad);
    } else {
      setResolvedAll(true);
    }
  };

  const handleStrategy = async (selectedStrategy) => {
    if (applyToAll) {
      do {
        const newResolvedList = duplicatesPage.content.map(duplicate => ({
          partyId: duplicate.party.id,
          strategy: selectedStrategy,
        }));
        storeResolvedList(newResolvedList);
        loadNextPage(duplicatesPage.totalElements);
      } while (hasAnotherPage);
    } else {
      storeResolvedList([{ partyId: currentDuplicate.party.id, strategy: selectedStrategy }]);
      if (isLastDuplicate) {
        loadNextPage(10);
      } else {
        setCurrentDuplicateIndex(currentDuplicateIndex + 1);
      }
    }
  };

  const onStrategyChange = ev => setStrategy(ev.target.value);
  const onApplyToAllChange = () => setApplyToAll(!applyToAll);
  const onImport = () => {
    handleStrategy(strategy);
    setStrategy(null);
  };

  return {
    strategy,
    applyToAll,
    duplicatesPage,
    currentDuplicate,
    resolvedList,
    setApplyToAll,
    onStrategyChange,
    onApplyToAllChange,
    onImport,
    onCancel,
    showCancelConfirm,
    setShowCancelConfirm,
  };
}
